import {Component} from '@angular/core';
import { FILE_API_PATH } from '../../../../services/http/utils/httpSettings';
import { FrontOtpBusinessService } from '../../../../services/business/otpBusiness.service';
import { FrontOtpHttpService } from '../../../../services/http/otpHttp.service';
import { FrontPlugBusinessService } from '../../../../services/business/plugBusiness.service';
import { FrontStorageService } from '../../../../services/storage/storage.service';
import { IziviaLoggerService } from '../../../../services/common/iziviaLogger.service';
import { Media } from '../../../../domain/media/media';
import { MediaFamilyType } from '../../../../domain/enum/mediaFamilyType';
import { MediaSummaryDto } from '../../../../domain/media/mediaSummary';
import { OTPSession } from '../../../../domain/otp/otp';
import { OtpSessionDto } from '../../../../domain/otp/otp';
import { OtpSessionHttpService } from '../../../../services/http/otpSessionHttp.service';
import { OtpSessionRequest } from '../../../../domain/otp/otp';
import { OTPSessionStatus } from '../../../../domain/otp/otp';
import { PaymentSource } from '../../../../domain/enum/paymentSource';
import { PricingContextDto } from '../../../../domain/map/chargeAreaDetailsMarker';
import { StationDto } from '../../../../domain/infra/station/station';
import { SupportContact } from '../../../../domain/operator/frontEndConfig';
import {ActivatedRoute} from '@angular/router';
import {NavController, ViewDidEnter, ViewWillEnter, ViewWillLeave} from '@ionic/angular';
import { CurrentEnvironmentConfigService } from '../../../../services/common/currentEnvironmentConfig.service';
import { IziviaToastService } from '../../../../services/common/iziviaToast.service';
import { IziviaUriOpenerService } from '../../../../services/common/iziviaUriOpener.service';
import { ModalControllerService } from '../../../../services/common/modalController.service';
import { NewChargeContext } from '../../../../services/business/newChargeContext.service';
import { NewChargeStepType } from '../../../../domain/headerStep';
import { PAYNOW_USER_EMAIL } from '../../../../services/common/storage/iziviaStorage.keys';
import { SupportChoiceComponent } from '../../../../components/supportChoice/supportChoice.component';
import {filter, finalize, first, map, startWith, switchMap, take, tap} from 'rxjs/operators';
import {forkJoin, interval, Subscription, timer} from 'rxjs';
import {FrontEndService} from '../../../../services/frontEnd.service';
import {compact, find, isEmpty} from 'lodash-es';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'media-choice-view',
    templateUrl: './mediaChoice.view.html',
    styleUrls: ['./mediaChoice.view.scss']
})
export class MediaChoiceView implements ViewWillEnter, ViewDidEnter, ViewWillLeave {
    /**
     * @ViewChild must be used because our form reference (<code>#mediaChoiceForm</code>) is within a <code>*ngIf</code>
     * expression which makes it invisible from outside
     *
     * @see https://stackoverflow.com/a/44812573/2118909
     */
    newChargeContext: NewChargeContext;
    step: NewChargeStepType = NewChargeStepType.MEDIA_CHOICE;
    station: StationDto | null;
    mediaSummary: MediaSummaryDto;
    userEmail: string;
    payingOtp: boolean;
    hasPricingInfo: boolean;
    otpPriceTextError: string;
    orderingOtp: boolean;
    supportContacts: SupportContact[];
    termsAndConditionsAccepted: boolean = false;
    termsAndConditionsCode?: string;
    tabSelected: 'getMedia' | 'enterMedia' = 'getMedia';
    otpCode: string;
    otpError: boolean = false;
    otpValid: boolean = false;
    isCheckingOtp: boolean = false;
    otpErrorKey: string;
    isAutoValidatingOtp: boolean = false;
    isGoingToNextSlide: boolean = true;
    enterMediaFormValid: boolean = false;

    private thirdPartyId: string;
    private otpSession: OTPSession;
    private pollingSubscription: Subscription;
    private getMediaFormValid: boolean = false;

    constructor(private readonly route: ActivatedRoute,
        private readonly navController: NavController,
        private readonly otpHttpService: FrontOtpHttpService,
        private readonly toastService: IziviaToastService,
        private readonly plugBusinessService: FrontPlugBusinessService,
        private readonly otpSessionService: OtpSessionHttpService,
        private readonly modalControllerService: ModalControllerService,
        private readonly otpBusinessService: FrontOtpBusinessService,
        private readonly frontEndInfoService: FrontEndService,
        private readonly loggerService: IziviaLoggerService,
        private readonly currentEnvironmentConfigService: CurrentEnvironmentConfigService,
        private readonly urlOpenerService: IziviaUriOpenerService,
        private readonly otpService: FrontOtpHttpService,
        private readonly translateService: TranslateService,
        private readonly storageService: FrontStorageService
    ) {
        this.frontEndInfoService.currentFrontEndInfo$.subscribe(frontEndInfo => {
            this.thirdPartyId = frontEndInfo?.id;
            this.termsAndConditionsCode = frontEndInfo?.otpTermsAndConditionsI18nCode;
            if (frontEndInfo?.config?.supportConfig?.supportContacts?.length) {
                this.supportContacts = frontEndInfo.config.supportConfig.supportContacts;
            }
        });
    }

    ionViewWillEnter() {
        this.otpSession = null;
        this.station = this.route.snapshot.data.station;

        const language = localStorage.getItem('otpSession.language');
        if (language !== null && language !== this.translateService.currentLang) {
            this.translateService.use(language);
        }

        // It allows to hide screen portion inherited from previous state of that view when we go back to it.
        this.isGoingToNextSlide = false;

        // Could not resolved station (404, ...)
        if (this.station === null) {
            this.navController.navigateRoot('/app/new-charge/station-choice');
            return;
        }

        this.newChargeContext = this.route.snapshot.data.newChargeContext;
    }

    ionViewDidEnter() {
        this.hasPricingInfo = this.computeHasPricingInfo(this.station?.pricingContext);

        if (!!this.newChargeContext.otpSession.otpCode) {
            this.tabSelected = 'enterMedia';
            this.otpCode = this.newChargeContext.otpSession.otpCode;

            if (!this.userEmail) {
                this.storageService.get<string>(PAYNOW_USER_EMAIL).subscribe((email?: string) => {
                    this.userEmail = email;

                    if (!!this.userEmail && !!this.otpCode) {
                        this.isAutoValidatingOtp = true;
                        this.checkOtpIsValid();
                    }
                });
            }
        }
    }

    ionViewWillLeave() {
        this.pollingSubscription?.unsubscribe();
    }

    openSupportModal() {
        if (this.supportContacts) {
            this.modalControllerService.display({
                component: SupportChoiceComponent,
                componentProps: {
                    thirdPartyId: this.thirdPartyId,
                    stationAlias: this.newChargeContext.chargePoint.stationAlias
                }
            }).subscribe();
        }
    }

    order() {
        this.orderingOtp = true;
        const otpSessionRequest: OtpSessionRequest = this.buildOtpSessionRequest();

        this.otpHttpService.orderOtp(this.newChargeContext.chargePoint.stationAlias, otpSessionRequest)
            .pipe(finalize(() => this.orderingOtp = false))
            .subscribe((media: Media) => {
                this.mediaSummary = {
                    id: media._id,
                    expirationDate: media.expirationDate,
                    name: media.code,
                    visual: {
                        code: media.publicCode || media.code // media.code in case publicCode would be null
                    },
                    familyType: media.family.type || MediaFamilyType.DEFAULT
                };

                this.goToNextSlide();
            });
    }

    pay() {
        this.payingOtp = true;

        if (this.otpSession) {
            this.payOtpSession(this.otpSession);
        } else {
            const otpSessionRequest: OtpSessionRequest = this.buildOtpSessionRequest();
            this.otpHttpService.openOtpSession(otpSessionRequest).subscribe(
                (otpSession: OTPSession) => {
                    this.otpSession = otpSession;
                    this.payOtpSession(otpSession);
                },
                error => {
                    this.payingOtp = false;
                    this.toastService.showServerErrorOrFallback(error, 'otp.error.default');
                }
            );
        }
    }

    goToNextSlide() {
        this.newChargeContext.mediaSummary = this.mediaSummary;
        this.newChargeContext.otpSession.otpCode = this.otpSession.otpCode;

        this.isGoingToNextSlide = false;
        this.navController.navigateForward([`/app/new-charge/station/${this.station.id}/summary`], {
            queryParams: this.newChargeContext.toQueryParam()
        });
    }

    goToPreviousSlide() {
        this.newChargeContext.mediaSummary = undefined;
        // Unset charging infra information
        // These information will be set by previous slides according to other properties on newChargeContext
        this.newChargeContext.otpSession.stationRef = undefined;
        this.newChargeContext.otpSession.chargePointRef = undefined;
        this.newChargeContext.otpSession.plugRef = undefined;
        const nextStep: string = this.newChargeContext.isReservable ? 'reservation-choice' : 'charge-point-choice';
        this.navController.navigateBack([`/app/new-charge/station/${this.station.id}/${nextStep}`], {
            queryParams: this.newChargeContext.toQueryParam()
        });
    }

    canGoNext() {
        return !!this.mediaSummary;
    }

    canPay() {
        return !this.payingOtp
            && this.getMediaFormValid;
    }

    goToHome() {
        this.navController.navigateRoot(['/home'], {
            replaceUrl: true
        });
    }

    private buildOtpSessionRequest(): OtpSessionRequest {
        return {
            chargePointRef: this.newChargeContext.chargePoint.id,
            chargePointVisualId: this.newChargeContext.chargePoint.visualId,
            stationAlias: this.newChargeContext.chargePoint.stationAlias,
            orientation: this.plugBusinessService.plugOrientationToString(this.plugBusinessService.computePlugOrientation(
                this.newChargeContext.plugTypeChoice.plugType.userCableRequirement,
                this.newChargeContext.plugTypeChoice.hasPlugCable,
                true
            )),
            clientSource: PaymentSource.MOBILE,
            outletModel: this.newChargeContext.plugTypeChoice.plugType.name,
            userEmail: this.userEmail,
            reservationDuration: this.newChargeContext.hasReservation() ?
                this.newChargeContext.reservation.durationInMinutes * 60 : null
        };
    }

    private payOtpSession(otpSession: OTPSession) {
        switch (otpSession.status) {
            case OTPSessionStatus.OPEN:
            case OTPSessionStatus.WAIT_FOR:
            case OTPSessionStatus.WAIT_RESERVATION:
            case OTPSessionStatus.IGNORED_BY_USER:
                if (otpSession.price === 0 && otpSession.freeCharge === true) {
                    this.otpHttpService.closeOtpSession(otpSession._id).subscribe(
                        () => {
                            this.pollOtpSessionUntilEndedAndGoToNextSlide(otpSession._id);
                        },
                        () => {
                            this.payingOtp = false;
                            this.toastService.showError('otp.error.default');
                        }
                    );
                } else {
                    let httpRequest;
                    this.otpSessionService.buyOtpSession(otpSession._id, this.newChargeContext.id).
                        pipe(
                            tap(req => httpRequest = req),
                            switchMap(_ => this.otpHttpService.getOtpSession(otpSession._id))
                        ).subscribe({
                            next: (session: OTPSession) => {
                                this.treatEndedOtpSession(session, false);
                                this.newChargeContext.otpSession._id = otpSession._id;
                                localStorage.setItem(this.newChargeContext.id, JSON.stringify(this.newChargeContext));
                                window.location.href = httpRequest.url;
                            },
                            error: (error) => {
                                if (error && error.error) {
                                    switch (error.error['labelKey']) {
                                        case 'error.otp.paymentOrderStatus.PAID':
                                            this.loggerService.debug('[payOtpSession] Previous payment order has been finally paid');
                                            this.pollOtpSessionUntilEndedAndGoToNextSlide(otpSession._id);
                                            return;
                                        case 'error.otp.sessionExpired':
                                            this.loggerService.debug('[payOtpSession] Otp session has expired: open a new session');
                                            this.otpSession = undefined;
                                            this.pay();
                                            return;
                                    }
                                }

                                this.payingOtp = false;
                                this.loggerService.error('Cannot open payment modal', error);
                                this.toastService.showError('otp.error.price.setup');
                            }
                        });
                }
                break;
            case OTPSessionStatus.TIMEOUT:
                this.payingOtp = false;
                this.toastService.showError('server.error.otp.sessionExpired');
                break;
            case OTPSessionStatus.ENDED:
                this.pollOtpSessionUntilEndedAndGoToNextSlide(otpSession._id);
                break;
            case OTPSessionStatus.ERROR:
                this.payingOtp = false;
                this.toastService.showError('otp.error.default');
                break;
        }
    }

    private pollOtpSessionUntilEndedAndGoToNextSlide(otpSessionId: string) {
        this.pollingSubscription = interval(5 * 1000) // Every 5 seconds
            .pipe(
                startWith(0),
                take(12), // Times out after one minute
                switchMap(() => this.otpHttpService.getOtpSession(otpSessionId)),
                filter(otpSession => !!otpSession),
                filter(otpSession => !this.otpBusinessService.canEvolve(otpSession)),
                first(),
                map(otpSession => {
                    if (otpSession.status === OTPSessionStatus.ENDED) {
                        return otpSession;
                    }

                    throw new Error(otpSession.status);
                }),
                finalize(() => this.payingOtp = false)
            )
            .subscribe(
                endedOtpSession => this.treatEndedOtpSession(endedOtpSession),
                error => {
                    this.loggerService.warning(`[pollOtpSessionUntilEndedAndGoToNextSlide] Otp session polling stopped.`,
                        otpSessionId, error);
                    this.toastService.showError('otp.error.default');
                    this.navController.navigateRoot('/events', {replaceUrl: true});
                }
            );
    }

    private treatEndedOtpSession(endedOtpSession, goToNextSlide = true) {
        this.otpSession = endedOtpSession;

        // Always persists locally the email used for otp session
        this.storageService
            .set(PAYNOW_USER_EMAIL, endedOtpSession.userEmail)
            .subscribe(() => {});

        this.mediaSummary = {
            id: this.otpSession.mediaRef,
            expirationDate: this.otpSession.otpExpirationDate,
            name: this.otpSession.otpCode,
            visual: {
                code: this.otpSession.otpCode
            },
            familyType: this.otpSession.otpType || MediaFamilyType.DEFAULT
        };

        if (goToNextSlide) {
            this.goToNextSlide();
        } else {
            this.newChargeContext.mediaSummary = this.mediaSummary;
            this.newChargeContext.otpSession.otpCode = this.otpSession.otpCode;
        }
    }

    private computeHasPricingInfo(pricingContext: PricingContextDto): boolean {
        return pricingContext
            && pricingContext.pricingCodesBySubscriptionRef
            && Object.keys(pricingContext.pricingCodesBySubscriptionRef).length > 0
            && (Object.keys(pricingContext.pricingCodesBySubscriptionRef).length > 1
                || !!find(Object.values(pricingContext.pricingCodesBySubscriptionRef), (item) => !isEmpty(compact(item))));
    }

    openCgu() {
        this.currentEnvironmentConfigService.getCurrentDocumentBaseUrl()
            .subscribe(baseUrl => this.urlOpenerService.open(baseUrl + FILE_API_PATH + this.termsAndConditionsCode));
    }

    onMediaFormValidationChanged(isValid: boolean) {
        this.getMediaFormValid = isValid;
    }

    onEnterMediaFormValidationChanged(isValid: boolean) {
        this.enterMediaFormValid = isValid;
    }

    updateUserEmail(email: string) {
        this.userEmail = email;
    }

    onOtpCodeChanged(otpCode: string) {
        this.otpCode = otpCode;
        this.newChargeContext.otpSession.otpCode = otpCode;
    }

    goToSummary(otpSession: OtpSessionDto) {
        this.isGoingToNextSlide = true;

        this.otpSession = otpSession;
        this.otpHttpService.getActiveOtpMediaByCode(this.otpCode).subscribe((media: Media) => {
            this.mediaSummary = {
                id: media._id,
                expirationDate: otpSession.expirationDate,
                name: otpSession.otpCode,
                visual: {
                    code: otpSession.otpCode
                },
                familyType: media?.family?.type || MediaFamilyType.OTP_CB
            };

            this.goToNextSlide();
        });
    }

    checkOtpIsValid() {
        this.isCheckingOtp = true;
        this.otpError = false;
        this.otpValid = false;

        forkJoin({
            // Force a minimal duration to 1 sec. It avoid to display loading icon for a too small time.
            timer: timer(1000),
            otpSession: this.otpService.findUnexpiredOtpByCode(this.otpCode, this.station.id, this.userEmail),
        }).pipe(
            map(({ otpSession }) => otpSession)
        ).subscribe({
            next: (value: OtpSessionDto) => {
                this.otpError = !value;
                this.goToSummary(value);
            },
            error: (err) => {
                this.otpError = true;
                this.otpValid = false;
                this.isCheckingOtp = false;
                this.otpErrorKey = err.error?.labelKey != null
                    ? err.error.labelKey
                    : 'charge.mediaChoice.otpErrorMessage';
            },
            complete: () => {
                this.isCheckingOtp = false;
                this.otpValid = true;
                this.isAutoValidatingOtp = false;
            }
        });
    }
}
